import { useEffect, useState } from "react";
import { X, FileDown, PlayCircle, Music, Image as ImageIcon, Folder } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import PdfViewer from "@/components/PdfViewer";

const CONFIG = {
  phototheque: {
    contentKey: "bm.musee.phototheque",
    defaults: { eyebrow: "Musée universitaire", title: "Photothèque", banner_image: "/media/museum-photos.jpg", intro: "Photographies patrimoniales retraçant l'histoire médiatique et académique." },
  },
  presse_ecrite: {
    contentKey: "bm.musee.presse_ecrite",
    defaults: { eyebrow: "Musée universitaire", title: "Musée de la presse écrite congolaise", banner_image: "/media/museum-publications.jpg", intro: "Archives de la presse écrite congolaise : journaux, magazines et publications historiques." },
  },
  audiovisuel: {
    contentKey: "bm.musee.audiovisuel",
    defaults: { eyebrow: "Musée universitaire", title: "Musée de l'audiovisuel congolais", banner_image: "/media/museum-events.jpg", intro: "Radiodiffusion, télévision et productions audiovisuelles patrimoniales de la RDC." },
  },
  cinema: {
    contentKey: "bm.musee.cinema",
    defaults: { eyebrow: "Musée universitaire", title: "Musée du cinéma congolais", banner_image: "/media/hero-university.jpg", intro: "Œuvres, cinéastes et évolution du cinéma congolais." },
  },
  tic: {
    contentKey: "bm.musee.tic",
    defaults: { eyebrow: "Musée universitaire", title: "Musée des Technologies de l'Information et de la Communication en RDC", banner_image: "/media/research-lab.jpg", intro: "Panorama de l'évolution des TIC et du numérique en République Démocratique du Congo." },
  },
};

const MEDIA_TABS = [
  { key: "", label: "Tout", Icon: Folder },
  { key: "image", label: "Photos", Icon: ImageIcon },
  { key: "video", label: "Vidéos", Icon: PlayCircle },
  { key: "audio", label: "Audios", Icon: Music },
  { key: "pdf", label: "Documents", Icon: FileDown },
];

export default function MuseumThematique({ section }) {
  const cfg = CONFIG[section];
  const [content, setContent] = useState(cfg.defaults);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(`/content/${cfg.contentKey}`).then((r) => setContent({ ...cfg.defaults, ...(r.data || {}) })).catch(() => {});
    api.get(`/museum/${section}`).then((r) => setItems(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [section]);

  const filtered = filter ? items.filter((i) => (i.media_type || "image") === filter) : items;

  // Group by collection
  const grouped = filtered.reduce((acc, it) => {
    const col = it.collection?.trim() || "Général";
    (acc[col] = acc[col] || []).push(it);
    return acc;
  }, {});
  const collections = Object.keys(grouped);

  return (
    <div data-testid={`musee-thematique-${section}`}>
      <section className="relative h-[42vh] min-h-[300px] overflow-hidden">
        <img src={content.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{content.title}</h1>
          <p className="mt-3 text-white/85 max-w-3xl">{content.intro}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="media-tabs">
          {MEDIA_TABS.map((t) => (
            <button key={t.key} onClick={() => setFilter(t.key)} className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-sm border transition ${filter === t.key ? "bg-unisic-blue text-white border-unisic-blue" : "bg-white border-slate-300 text-slate-700 hover:border-unisic-blue"}`} data-testid={`tab-media-${t.key || "all"}`}>
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {collections.length === 0 && (
          <div className="text-center py-20 paper-card p-10">
            <p className="text-slate-500 text-sm">Aucun élément dans cette section pour l'instant.</p>
          </div>
        )}

        {collections.map((col) => (
          <div key={col} className="mb-14" data-testid={`collection-${col}`}>
            <div className="flex items-center gap-3 mb-6">
              <Folder className="w-5 h-5 text-unisic-gold" />
              <h2 className="text-2xl font-bold text-unisic-blue">{col}</h2>
              <span className="text-sm text-slate-500">({grouped[col].length})</span>
            </div>
            <div className="grid gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {grouped[col].map((it) => (
                <ItemCard key={it.id} it={it} onClick={() => setPreview(it)} />
              ))}
            </div>
          </div>
        ))}
      </section>

      {preview && <ItemModal item={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function ItemCard({ it, onClick }) {
  const mt = it.media_type || "image";
  const IconMap = { image: ImageIcon, video: PlayCircle, audio: Music, pdf: FileDown };
  const Icon = IconMap[mt] || ImageIcon;
  return (
    <button onClick={onClick} className="group relative overflow-hidden rounded-sm aspect-square bg-slate-100 text-left" data-testid={`item-${it.id}`}>
      {it.image_url ? (
        <img src={it.image_url} alt={it.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-unisic-blue to-unisic-blue/70 flex items-center justify-center">
          <Icon className="w-10 h-10 text-white/70" />
        </div>
      )}
      <div className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/60 text-white">
        <Icon className="w-4 h-4" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
        <div className="text-white text-sm font-semibold truncate">{it.title}</div>
        {it.description && <div className="text-white/80 text-xs line-clamp-2">{it.description}</div>}
      </div>
    </button>
  );
}

function ItemModal({ item, onClose }) {
  const mt = item.media_type || "image";
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-4 py-8 overflow-auto" onClick={onClose} data-testid="item-modal">
      <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end mb-2">
          <button onClick={onClose} className="p-2 rounded bg-white/10 text-white hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
        <div className="bg-white rounded-sm overflow-hidden">
          {mt === "image" && item.image_url && (
            <img src={item.image_url} alt={item.title} className="w-full max-h-[70vh] object-contain bg-black" />
          )}
          {mt === "video" && (
            <div className="aspect-video bg-black">
              {item.file_path ? (
                <video controls className="w-full h-full"><source src={fileUrl(item.file_path)} /></video>
              ) : item.video_url ? (
                <iframe src={item.video_url} className="w-full h-full" allowFullScreen title={item.title} />
              ) : (
                <div className="p-8 text-white text-center">Aucune vidéo disponible.</div>
              )}
            </div>
          )}
          {mt === "audio" && (
            <div className="p-8">
              {item.image_url && <img src={item.image_url} alt="" className="w-full h-56 object-cover rounded-sm mb-4" />}
              {(item.file_path || item.audio_url) ? (
                <audio controls className="w-full"><source src={item.file_path ? fileUrl(item.file_path) : item.audio_url} /></audio>
              ) : (
                <div className="text-slate-500 text-sm">Aucun fichier audio.</div>
              )}
            </div>
          )}
          {mt === "pdf" && item.file_path && (
            <div className="p-4"><PdfViewer url={fileUrl(item.file_path)} filename={`${item.title}.pdf`} /></div>
          )}
          <div className="p-6">
            <h3 className="font-display font-semibold text-unisic-blue text-xl">{item.title}</h3>
            {item.collection && <div className="text-[11px] uppercase tracking-widest text-unisic-gold mt-1">{item.collection}</div>}
            {item.description && <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{item.description}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
